import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FiChevronRight, FiShield } from 'react-icons/fi';
import { accentFor, badgeFor } from '../notifications/badges';
import { onPill } from '../notifications/pillBus';
import { useNotificationTap } from '../notifications/useNotificationTap';
import './InAppNotificationPill.css';

/**
 * The notification shown when the app is ALREADY IN FRONT (spec §6).
 *
 * It exists because a system banner over an app you are looking at is noise:
 * the service worker suppresses the banner and posts the payload in here
 * instead. Two platforms are deliberately not covered — the iOS PWA, where
 * Apple revokes the subscription unless a push shows a real notification (see
 * the `push` handler), and anything not in the foreground.
 *
 * ONE AT A TIME, FIFO, AT MOST THREE WAITING. A society with four cards on the
 * go can produce a burst, and a stack of pills would cover the screen a member
 * is trying to score on. The fourth is dropped rather than queued.
 */
export const HOLD_MS = 4000;
export const EXIT_MS = 450;
export const MAX_QUEUE = 3;

// Far enough that a flick counts and a scroll does not.
const SWIPE_DISMISS_PX = 40;

const InAppNotificationPill = () => {
  const [queue, setQueue] = useState([]);
  const [leaving, setLeaving] = useState(false);
  const onTap = useNotificationTap();
  const touchStartY = useRef(null);

  useEffect(() => onPill((data) => {
    if (!data) return;
    setQueue((q) => (q.length >= MAX_QUEUE ? q : [...q, data]));
  }), []);

  const current = queue[0];

  const dismiss = useCallback(() => setLeaving(true), []);

  // Hold, then leave. Keyed on the item so a new one at the front restarts
  // the clock rather than inheriting the last one's remaining time.
  useEffect(() => {
    if (!current || leaving) return undefined;
    const t = setTimeout(() => setLeaving(true), HOLD_MS);
    return () => clearTimeout(t);
  }, [current, leaving]);

  // …then drop it once the exit has played.
  useEffect(() => {
    if (!leaving) return undefined;
    const t = setTimeout(() => {
      setQueue((q) => q.slice(1));
      setLeaving(false);
    }, EXIT_MS);
    return () => clearTimeout(t);
  }, [leaving]);

  if (!current) return null;

  const badge = current.badge || 'start';
  // §5: the society is absent from the payload unless the RECIPIENT belongs to
  // more than one, which the server decides — so the shield and the name
  // appear under exactly the same rule as the body's suffix, and this
  // component never has to know what the rule is.
  const society = current.society || '';
  // The eyebrow now leads with the event's own emoji (e.g. "🦅 EAGLE · HOLE
  // 12"), built server-side so the pill and the lock screen cannot drift.
  const label = current.eyebrow || '';
  const title = current.title || '';

  const handleTap = () => {
    dismiss();
    onTap(current);
  };

  return (
    <button
      type="button"
      className={`inap-pill${leaving ? ' inap-pill--leaving' : ''}`}
      // The society and the headline, in that order — the same thing the
      // eyebrow says, for someone who cannot see it.
      aria-label={society ? `${society}: ${title}` : title}
      onClick={handleTap}
      onTouchStart={(e) => { touchStartY.current = e.touches[0]?.clientY ?? null; }}
      onTouchMove={(e) => {
        if (touchStartY.current === null) return;
        if ((e.touches[0]?.clientY ?? 0) - touchStartY.current < -SWIPE_DISMISS_PX) {
          touchStartY.current = null;
          dismiss();
        }
      }}
      onTouchEnd={() => { touchStartY.current = null; }}
    >
      <img className="inap-badge" src={badgeFor(badge)} alt="" aria-hidden="true" />

      <span className="inap-copy">
        <span className="inap-eyebrow">
          {society ? (
            <>
              <FiShield className="inap-shield" aria-hidden="true" />
              {/* The ONLY part that truncates. A clipped society name still
                  says which club; a clipped "EAGLE" says nothing. */}
              <span className="inap-society">{society}</span>
              <span className="inap-dot" aria-hidden="true">·</span>
            </>
          ) : null}
          <span className="inap-event" style={{ color: accentFor(badge) }}>{label}</span>
        </span>
        <span className="inap-title">{title}</span>
      </span>

      <FiChevronRight className="inap-chevron" aria-hidden="true" />
    </button>
  );
};

export default InAppNotificationPill;
